import { FC, useEffect, useRef, useState } from "react";
import { styled } from "styled-components";
import { Entry } from "contentful";
import { SwiperCarousel, SwiperSlide } from "./Swiper";
import SearchspringProductTile from "./SearchspringProductTile";
import { buttonReset, colors, font } from "../lib/sharedStyles";
import { Tracker } from "@searchspring/snap-tracker";
import { Logger } from "@searchspring/snap-logger";
import { Profiler } from "@searchspring/snap-profiler";
import { EventManager } from "@searchspring/snap-event-manager";
import { UrlManager, UrlTranslator } from "@searchspring/snap-url-manager";
import { Client } from "@searchspring/snap-client";
import { RecommendationController } from "@searchspring/snap-controller";
import { RecommendationStore } from "@searchspring/snap-store-mobx";

const CarouselWrapper = styled.div`
  flex-direction: column;
  margin-bottom: 80px;

  @media (min-width: 1024px) {
    margin-bottom: 120px;
  }
`;

const CarouselContainer = styled.div<{ $hasHeading: boolean; }>`
  width: 100%;
  margin-left: -1rem;

  @media (min-width: 1024px) {
    margin-top: ${({ $hasHeading }) => ($hasHeading ? '0' : '32px')};
  }

  swiper-slide {
    height: auto;

    > div {
      height: 100%;
    }
  }
`;

const CarouselHeader = styled.div<{ $hasHeading: boolean; $forceMinHeight: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $hasHeading }) => ($hasHeading ? 'space-between' : 'flex-end')};

  h2 {
    ${font('Futura', 600, '24px', '36px')};
    letter-spacing: -0.48px;
    color: ${colors.fender.primaryDark};
  }

  @media (min-width: 768px) {
    h2 {
      ${font('Futura', 600, '32px', '34px')};
      letter-spacing: -0.72px;
    }
  }

  @media (min-width: 1024px) {
    margin-top: ${({ $hasHeading }) => ($hasHeading ? '0' : '-64px')};
    min-height: ${({ $forceMinHeight }) => $forceMinHeight ? '32px' : '0'};
  }
`;

const Heading = styled.h2`
  margin: 0;
  margin-bottom: 24px;

  @media (min-width: 1024px) {
    margin-bottom: 32px;
  }
`;

const Navigation = styled.div<{ $hasHeading: boolean }>`
  display: none;
  gap: 8px;
  height: ${({ $hasHeading }) => ($hasHeading ? '3.44rem' : 'auto')};

  @media (min-width: 1024px) {
    display: flex;
  }

  button {
    ${buttonReset()};
    width: 32px;
    height: 32px;
    transition: background-color 0.2s ease, border-radius 0.2s ease;
    cursor: pointer;

    &:hover {
      background-color: #f5f5f5;
      border-radius: 100%;
    }

    svg {
      vertical-align: middle;
    }

    // Rotate "next" arrow
    &:not(:first-of-type) {
      svg {
        transform: rotate(180deg);
      }
    }

    // This class is dynamically added by Swiper.
    &.swiper-button-disabled {
      svg path {
        fill: #E5E5E5;
      }
    }
  }
`;

const swiperParams = {
  mousewheel: {
    enabled: true,
    forceToAxis: true,
    sensetivity: 1,
  },
  breakpoints: {
    768: {
      slidesPerView: 3.9,
      spaceBetween: 16
    }
  },
  keyboard: {
    enabled: true,
    onlyInViewport: true
  },
  slidesOffsetAfter: 16,
  slidesOffsetBefore: 16,
  slidesPerView: 1.95,
  spaceBetween: 8,
};

interface SearchspringCarouselProps extends Entry {
  id: string,
  fields: {
    heading?: string;
    searchspringTags: string;
  }
}

const SearchspringCarousel: FC<SearchspringCarouselProps> = ({
  id,
  sys,
  fields: {
    heading,
    searchspringTags
  }
}) => {
  if (!searchspringTags) return null;

  const [products, setProducts] = useState([]);

  // Generate a unique id so it persists for the lifetime of this instance.
  const uniqueCarouselId = useRef(`${sys.id}-${Math.random().toString(36).substring(2, 9)}`);
  // These attributes are needed to display correct pricing in EMEA
  const localeDataEl = document.getElementById('basic-modal-entry');
  const shopLocale = localeDataEl?.dataset.shopLocale;
  const shopMarket = localeDataEl?.dataset.shopMarket;
  const regionDataEl = document.getElementById('react-jwt-all');
  const siteRegion = regionDataEl?.dataset.region;

  useEffect(() => {
    const searchspringConfigEl = document.getElementById('react-searchspring-config');
    const siteId = searchspringConfigEl?.dataset.siteId;

    if (!siteId) return;

    const config = {
      id: `recommend-${sys.id}`,
      tag: searchspringTags
    }

    // If this carousel is being rendered on a PDP, pull the product id
    // to provide as contextual data to the Searchspring API.
    let productId;
    const bodyTag = document.querySelector('body');
    if (bodyTag && bodyTag.classList.contains('product-page-template')) {
      const atcButton = document.getElementById('atc-button');
      if (atcButton?.dataset.productId) {
        productId = atcButton?.dataset.productId;
      }
    }

    const urlManager = new UrlManager(new UrlTranslator());
    // Todo: use one recommendation controller for all carousels?
    const controller = new RecommendationController(config, {
      client: new Client({ siteId }),
      store: new RecommendationStore(config, { urlManager }),
      urlManager,
      eventManager: new EventManager(),
      profiler: new Profiler(),
      logger: new Logger(),
      tracker: new Tracker({ siteId }),
    });

    controller.on('afterSearch', async (eventData, next) => {
      // @ts-ignore: No type in snapi package for this.
      setProducts(eventData?.response?.results || []);
      await next();
    });

    /**
     * It seems the only way to provide product data to the recommendation engine is to call
     * this client method. For whatever reason, requesting something like "recently-viewed"
     * while also sending a product id returns nothing. So to limit the impact of this change,
     * we'll only use the alternate request method for these two cases on the PDP.
     */
    if (productId && (searchspringTags.includes('similar') || searchspringTags.includes('cross-sell'))) {
      controller.client.recommend({
        tag: searchspringTags,
        products: [productId]
      }).then(res => {
        if (res.results) {
          // @ts-ignore
          setProducts(res.results);
        }
      });
    } else {
      controller.search();
    }
  }, []);

  if (!products) {
    return null;
  }

  const navigation = {
    prevEl: `.swiper-button-prev-${uniqueCarouselId.current}`,
    nextEl: `.swiper-button-next-${uniqueCarouselId.current}`,
  };

  const hasHeading = !!heading;
  const showNav = products.length >= 4;
  const forceMinHeight = !hasHeading && !showNav;

  return (
      <CarouselWrapper data-component-id={id}>
        <CarouselHeader $hasHeading={hasHeading} $forceMinHeight={forceMinHeight}>
        {heading && <Heading>{heading}</Heading>}
        <Navigation $hasHeading={hasHeading}>
          {Object.keys(navigation).map(key => (
            // @ts-ignore untyped object, substring to remove leading "."
            <button key={key} className={navigation[key].substring(1)}>
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12">
                <path fill='#191A1F' d="M11 6.988a.75.75 0 0 0 0-1.5v1.5ZM.47 5.708a.75.75 0 0 0 0 1.06l4.773 4.774a.75.75 0 1 0 1.06-1.061L2.061 6.238l4.242-4.242A.75.75 0 0 0 5.243.935L.47 5.708ZM11 5.488H1v1.5h10v-1.5Z"/>
              </svg>
            </button>
          ))}
        </Navigation>
      </CarouselHeader>
      <CarouselContainer $hasHeading={hasHeading}>
        <SwiperCarousel {...swiperParams} navigation={navigation}>
          {products.map(product => (
            // @ts-ignore
            <SwiperSlide key={product.id}>
              <SearchspringProductTile
                layout="grid"
                locale={shopLocale || 'en'}
                shopMarket={shopMarket}
                region={siteRegion}
                product={product}
              />
            </SwiperSlide>
          ))}
        </SwiperCarousel>
      </CarouselContainer>
    </CarouselWrapper>
  );
}

export default SearchspringCarousel;
